/**
 * The no-pressure upgrade modal — ported from
 * `design-handoff/tabvault-ds/ui_kits/vault/Vault Free Gating.dc.html`,
 * with plan copy and the monthly/yearly toggle idiom shared with
 * PricingPage.tsx so the two surfaces never drift.
 *
 * "Stay on Free" is always visible and never guilt-trips. Checkout hands off
 * to the billing provider same-tab; when billing isn't wired yet the API
 * returns 501 and we say so plainly instead of erroring. If the account is
 * already Pro (via `api.account.usage()`), there is nothing to sell — the
 * primary button just acknowledges it and closes.
 */
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PRICING } from '@vault/shared';
import { Button, Icon, Modal } from '@vault/ui';
import { api, ApiError } from '../../api/client.js';
import { errMessage } from '../_vault/shared.js';
import { FreeVsProTable } from './gating.js';

type Interval = 'monthly' | 'yearly';

export interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Gated feature that opened the modal, e.g. "version history" — tailors the
   * headline ("Version history is a Pro feature") and highlights its table row.
   */
  feature?: string;
  /** Full headline override; wins over `feature`. */
  title?: string;
}

function sentence(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function UpgradeModal({ open, onClose, feature, title }: UpgradeModalProps) {
  const [interval, chooseInterval] = useState<Interval>('monthly');

  // Short-circuit for members: already-Pro accounts have nothing to buy here.
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.account.usage(),
    enabled: open,
    staleTime: 60_000,
  });
  const alreadyPro = usage.data?.plan === 'pro';

  const pricing = PRICING.pro;
  const perMonth = interval === 'yearly' ? Math.round(pricing.yearlyUsd / 12) : pricing.monthlyUsd;
  const discountPct = Math.round((1 - pricing.yearlyUsd / (pricing.monthlyUsd * 12)) * 100);

  const checkout = useMutation({
    mutationFn: (iv: Interval) => api.billing.checkout(iv),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const checkoutSoon =
    checkout.isError &&
    checkout.error instanceof ApiError &&
    (checkout.error.status === 501 || checkout.error.code === 'billing_not_configured');
  const checkoutFailed = checkout.isError && !checkoutSoon;

  function close() {
    checkout.reset();
    onClose();
  }

  function upgrade() {
    if (alreadyPro) {
      close();
      return;
    }
    if (checkout.isPending) return;
    checkout.mutate(interval);
  }

  const headline = title ?? (feature ? `${sentence(feature)} is a Pro feature` : 'Go Pro when you’re ready');

  return (
    <Modal
      open={open}
      width={560}
      title={headline}
      subtitle="Free stays generous. Pro connects everything."
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Stay on Free
          </Button>
          <Button loading={checkout.isPending} onClick={upgrade}>
            {alreadyPro ? 'You’re on Pro' : `Upgrade to Pro — $${perMonth}/mo`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <span style={{ display: 'inline-flex', border: '1px solid var(--line-strong)', borderRadius: 999, padding: 2, gap: 2 }}>
            <IntervalTab active={interval === 'monthly'} onClick={() => chooseInterval('monthly')}>
              Monthly ${pricing.monthlyUsd}
            </IntervalTab>
            <IntervalTab active={interval === 'yearly'} onClick={() => chooseInterval('yearly')}>
              Yearly ${pricing.yearlyUsd}{' '}
              <span style={{ color: 'var(--success)', fontFamily: 'var(--font-mono)', fontSize: 9 }}>−{discountPct}%</span>
            </IntervalTab>
          </span>
        </div>

        <FreeVsProTable highlight={feature} />

        {alreadyPro && (
          <span style={noteInfo}>
            <Icon name="check" size={12} color="var(--success)" />
            You’re already on Pro — everything here is unlocked.
          </span>
        )}
        {checkoutSoon && (
          <span style={noteInfo}>
            <Icon name="clock" size={12} color="var(--accent-ink)" />
            Checkout is opening soon — hang tight, we’ll email you the moment it’s live.
          </span>
        )}
        {checkoutFailed && (
          <span style={noteDanger}>
            <Icon name="x" size={12} color="var(--danger)" />
            Couldn’t start checkout — {errMessage(checkout.error)}
          </span>
        )}

        <span style={finePrint}>
          {interval === 'yearly' ? `BILLED $${pricing.yearlyUsd} YEARLY · ` : ''}
          CANCEL ANYTIME · LOCAL SAVES ARE NEVER LOCKED
        </span>
      </div>
    </Modal>
  );
}

/** Same pill-toggle idiom as PricingPage's interval switch. */
function IntervalTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: '6px 14px',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: 11.5,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--text-1)' : 'var(--text-2)',
        background: active ? 'var(--surface-3)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

const noteInfo: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  lineHeight: 1.45,
  color: 'var(--text-2)',
  background: 'var(--accent-tint)',
  border: '1px solid var(--line-accent)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};
const noteDanger: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 11.5,
  color: 'var(--danger)',
  background: 'var(--danger-tint)',
  border: '1px solid var(--danger-line)',
  borderRadius: 'var(--r-md)',
  padding: '9px 11px',
};
const finePrint: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  letterSpacing: '.06em',
  color: 'var(--text-3)',
  textAlign: 'center',
};
